"use client"

/* eslint-disable functional/immutable-data -- The modal owns and restores the body scroll lock. */
import { ArrowUpRight, Plus, X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useEffect, useId, useRef, useState } from "react"

import { BrickInquiryLink } from "@/components/brick/BrickInquiryLink"
import { contactHref } from "@/data/contact"
import type { GalleryTile } from "@/data/top-gallery"
import { trackEvent } from "@/lib/analytics"

import styles from "./GalleryTiles.module.css"

export function GalleryTilesView({ tiles }: { tiles: GalleryTile[] }) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [selected, setSelected] = useState<GalleryTile | null>(null)
  const titleId = useId()
  const modalTitleId = useId()

  useEffect(() => {
    const dialog = dialogRef.current
    if (!selected || !dialog) return
    const trigger = document.activeElement
    const overflow = document.body.style.overflow
    dialog.showModal()
    document.body.style.overflow = "hidden"
    return () => {
      dialog.close()
      document.body.style.overflow = overflow
      if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true })
    }
  }, [selected])

  if (tiles.length === 0) return null

  return (
    <section id="gallery" className={styles.section} aria-labelledby={titleId}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <div>
            <p className={styles.kicker}>YOSHINANI GALLERY</p>
            <h2 id={titleId} className={styles.heading}>
              つくった世界をめぐる
            </h2>
          </div>
          <p className={styles.intro}>
            LEGO、3Dプリント、工房のひとこま。
            <br />
            写真から、私たちのものづくりを。
          </p>
        </header>

        <ul className={styles.masonry} aria-label="制作作品と活動のギャラリー">
          {tiles.map((tile) => (
            <li key={tile.image.src} className={styles.tile}>
              <button
                type="button"
                className={styles.photo}
                onClick={() => {
                  setSelected(tile)
                  trackEvent("work_view", {
                    service_id: tile.service,
                    work_id: tile.workId,
                    cta_location: "top_gallery_photo",
                  })
                }}
                aria-label={`「${tile.title}」の写真を拡大する`}
                aria-haspopup="dialog"
              >
                <span className={styles.imageFrame}>
                  <Image
                    src={tile.image}
                    alt={tile.alt}
                    sizes="(max-width: 767px) 48vw, (max-width: 1199px) 24vw, 210px"
                    className={styles.image}
                    quality={80}
                    placeholder="blur"
                  />
                  <span className={styles.zoomHint} aria-hidden>
                    <Plus size={18} />
                  </span>
                </span>
                <span className={styles.caption} aria-hidden>
                  <span className={styles.workTitle}>{tile.title}</span>
                  <span className={styles.category}>{tile.category}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>

        <footer className={styles.footer}>
          <BrickInquiryLink
            href="/contact?service=yoshinani-brick"
            location="top_gallery"
            className={styles.inquiry}
          >
            LEGO制作を相談する <ArrowUpRight size={16} aria-hidden />
          </BrickInquiryLink>
          <Link href="/gallery" className={styles.allWorks}>
            すべての写真を見る <ArrowUpRight size={18} aria-hidden />
          </Link>
        </footer>
      </div>

      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby={modalTitleId}
        onClose={() => setSelected(null)}
        onCancel={() => setSelected(null)}
        onClick={(event) => {
          if (event.target === event.currentTarget) setSelected(null)
        }}
      >
        {selected && (
          <div className={styles.lightbox}>
            <button
              type="button"
              className={styles.close}
              aria-label="写真を閉じる"
              onClick={() => setSelected(null)}
            >
              <X size={24} aria-hidden />
            </button>
            <div className={styles.largeImage}>
              <Image
                src={selected.image}
                alt={selected.alt}
                fill
                sizes="(max-width: 1100px) 92vw, 1100px"
                unoptimized
                loading="eager"
                className={styles.contain}
                style={{ objectFit: "contain" }}
              />
            </div>
            <div className={styles.modalCaption}>
              <p id={modalTitleId}>{selected.title}</p>
              <BrickInquiryLink
                href={selected.inquiryHref ?? contactHref(selected.service)}
                workId={selected.workId}
                service={selected.service}
                location="top_gallery_photo"
                className={styles.modalInquiry}
                onNavigate={() => setSelected(null)}
              >
                {selected.service === "3d-print"
                  ? "3Dプリント制作を相談する"
                  : "LEGO制作を相談する"}{" "}
                <ArrowUpRight size={17} aria-hidden />
              </BrickInquiryLink>
            </div>
          </div>
        )}
      </dialog>
    </section>
  )
}
